/**
 * Hook que dado um CSS selector, retorna o bounding rect do elemento DOM.
 * Atualiza automaticamente ao resize, scroll e mutações do DOM.
 *
 * Retorna null se o elemento não for encontrado.
 */
import { useState, useEffect, useCallback } from 'react';

export interface ElementRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * @param selector - CSS selector do elemento alvo (ou null/undefined para não buscar)
 * @returns Rect do elemento ou null se não encontrado
 */
export function useElementRect(selector: string | null | undefined): ElementRect | null {
  const [rect, setRect] = useState<ElementRect | null>(null);

  const updateRect = useCallback(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(selector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  }, [selector]);

  useEffect(() => {
    // Atualiza imediatamente
    updateRect();

    // Observa mudanças no DOM (elementos que aparecem tardiamente)
    const mutObs = new MutationObserver(updateRect);
    mutObs.observe(document.body, { childList: true, subtree: true, attributes: true });

    // Observa resize da janela
    window.addEventListener('resize', updateRect, { passive: true });

    // Observa scroll de qualquer elemento
    window.addEventListener('scroll', updateRect, { passive: true, capture: true });

    return () => {
      mutObs.disconnect();
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, { capture: true });
    };
  }, [updateRect]);

  return rect;
}
